<!--
# -----------------------------------------------------------------------------
# Component: PolicyItemBen.vue
# Author: PST
# Created Date: 02/April/2019
# Comments: Policy Coverage -> Risk Item -> Benefit
# -----------------------------------------------------------------------------
-->
<template>
  <v-layout row wrap>
    <v-flex xs12>
      <v-toolbar dense color="grey lighten-3" flat>
        <v-spacer></v-spacer>
        <v-btn color="primary" dark class="mb-2" @click="showAddBenForm" v-show="enableBenBtn" :loading="policy.loading" v-if="!hideCRUD">
          New Benefit
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
      </v-toolbar>
      <v-data-table :headers="displayHeaders" :items="itemList" class="elevation-1" :loading="loading">
        <template v-slot:item="row">
          <tr>
            <td>{{ row.item.seqNo }}</td>
            <td>{{ row.item.benCode }}</td>
            <td>{{ row.item.benLongDesc }}</td>
            <td>{{ row.item.benLim }}</td>
            <td>{{ row.item.premRateAeFormatted }}</td>
            <td>{{ row.item.premRatePct }}</td>
            <td>{{ row.item.gapAeFormatted }}</td>
            <td>{{ row.item.gap }}</td>
            <td>{{ row.item.premDueAepFormatted }}</td>
            <td>{{ row.item.premDue }}</td>
            <td class="table-action-header" v-if="!hideCRUD">
              <table-action
                :rowData="row"
                review
                @review="doSelectBen(row.item)"
                :remove="enableBenBtn"
                @remove="doPromptRemoveBen(row.item)"
              ></table-action>
            </td>
          </tr>
        </template>
      </v-data-table>
      <confirm v-model="promptRemoveBen" @ok="doRemoveBen">{{ deleteBen }}</confirm>
    </v-flex>
    <v-flex xs12 v-if="showBen">
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-pencil</v-icon>
          <v-toolbar-title class="toolbar-title-3"
            >{{ actionMode }} Benefit {{ mainForm.seqNo !== undefined ? ' [' + mainForm.seqNo + ']' : '' }}</v-toolbar-title
          >
        </v-toolbar>
        <v-card-text>
          <v-layout row wrap>
            <v-flex lg3 md3 xs12>
              <v-autocomplete
                :items="benList"
                v-model="mainForm.benCode"
                :label="labels.benCode"
                item-text="label"
                item-value="value"
                data-vv-name="benCode"
                :data-vv-as="labels.benCode"
                :error-messages="errors.collect('benCode')"
                :loading="loading"
                clearable
                @change="calculateItemBen()"
                v-validate="'required'"
                :disabled="disableBenCode"
              ></v-autocomplete>
            </v-flex>
            <v-flex lg3 md3 xs12>
              <v-text-field
                v-model="mainForm.benLim"
                :label="labels.benLim"
                :error-messages="errors.collect('benLim')"
                data-vv-name="benLim"
                :data-vv-as="labels.benLim"
                v-num-mask="{ negative: false, intScale: 18, decScale: 2 }"
                :disabled="disableBenLim"
                v-validate="valBenLim"
                @blur="doBenLim"
              ></v-text-field>
            </v-flex>
            <v-flex lg3 md3 xs12>
              <v-autocomplete
                :items="aeList"
                label="Rate AE"
                v-model="mainForm.premRateAe"
                item-text="label"
                item-value="value"
                @change="doPremRateAe()"
                :disabled="protPrem"
              ></v-autocomplete>
            </v-flex>
            <v-flex lg3 md3 xs12>
              <v-text-field
                v-model="mainForm.premRatePct"
                :label="labels.premRatePct"
                :error-messages="errors.collect('premRatePct')"
                data-vv-name="premRatePct"
                :data-vv-as="labels.premRatePct"
                v-num-mask="{ negative: true, intScale: 18, decScale: 5 }"
                :disabled="mainForm.premRateAe"
                @blur="doPremRatePct()"
              ></v-text-field>
            </v-flex>
            <v-flex lg3 md3 xs12>
              <v-autocomplete
                :items="aeList"
                label="Gross Annual Premium AE"
                v-model="mainForm.gapAe"
                item-text="label"
                item-value="value"
                @change="doGapAe()"
                :disabled="disableAe"
              ></v-autocomplete>
            </v-flex>
            <v-flex lg3 md3 xs12>
              <v-text-field
                v-model="mainForm.gap"
                :label="labels.gap"
                :error-messages="errors.collect('gap')"
                data-vv-name="gap"
                :data-vv-as="labels.gap"
                @change="doPremDueAep()"
                v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                :disabled="mainForm.gapAe"
                :loading="gapLoading"
              ></v-text-field>
            </v-flex>
            <v-flex lg3 md3 xs12>
              <v-autocomplete
                :items="aepList"
                label="Premium Due AE"
                v-model="mainForm.premDueAep"
                item-text="label"
                item-value="value"
                @change="doPremDueAep()"
                :disabled="disableAe"
              ></v-autocomplete>
            </v-flex>
            <v-flex lg3 md3 xs12>
              <v-text-field
                v-model="mainForm.premDue"
                :label="labels.premDue"
                :error-messages="errors.collect('premDue')"
                data-vv-name="premDue"
                :data-vv-as="labels.premDue"
                v-num-mask="{ negative: true, intScale: 18, decScale: 2 }"
                :disabled="disablePd"
                :loading="premDueLoading"
              ></v-text-field>
            </v-flex>
            <v-flex xs12>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn depressed @click.prevent="doCloseItemBen()" :loading="policy.loading"><span class="btn-normal-error">Close</span></v-btn>
                <v-btn depressed type="submit" @click.prevent="doSave" :loading="policy.loading"
                  ><span class="btn-normal-primary">{{ labels.save }} Benefit</span></v-btn
                >
              </v-card-actions>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-flex>
    <v-dialog v-model="showErrorDialog" width="800px">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon>mdi-information</v-icon>
          <v-toolbar-title>System Information</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <div v-if="messages.length > 0" class="pb-3">
            <span class="info-dialog-msg-header">Oops something went wrong...</span>
            <v-spacer class="mt-2"></v-spacer>
            <ul class="showErrorMsgs">
              <li v-for="(error, index) in messages" :key="index">
                {{ error.message }}
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="showErrorDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import { expandURI } from '@/utils/ajax'
import MessageMixin from '@/mixins/message'
import { removeFormatNumber } from '@/utils/number'
import { formatPrice } from '@/utils/index'
import * as TableConst from '@/mixins/table'
import { isEqual } from '@/utils/compare'
import clone from 'clone'
export default {
  data() {
    return {
      labels: {
        benefitsTitle: 'Benefits',
        tableTitle: 'Item Benefits Summary',
        delBen: 'Delete Benefit',
        editBen: 'Edit Benefit',
        addBen: 'Add Benefit',
        save: 'Save',
        reset: 'Reset',
        benCode: 'Benefit',
        benLim: 'Benefit Limit',
        premRateAe: 'Rate A/E',
        premRatePct: 'Rate %',
        gapAe: 'GAP A/E',
        gap: 'Gross Annual Premium',
        premDueAep: 'PD A/E/P',
        premDue: 'Premium Due'
      },
      headers: [
        { text: 'S/N', align: 'left', value: 'seqNo' },
        { text: 'Benefit', align: 'left', value: 'benCode' },
        { text: 'Description', value: 'benDesc' },
        { text: 'Benefit Limit', value: 'benLim' },
        { text: 'Rate A/E', value: 'premRateAeFormatted' },
        { text: 'Rate %', value: 'premRatePct' },
        { text: 'GAP A/E', value: 'gapAeFormatted' },
        { text: 'GAP', value: 'gap' },
        { text: 'PD A/E/P', value: 'premDueAepFormatted' },
        { text: 'PD', value: 'premDue' },
        { text: 'Action', value: 'action', align: 'center' }
      ],
      headersWOAction: [
        { text: 'S/N', align: 'left', value: 'seqNo' },
        { text: 'Benefit', align: 'left', value: 'benCode' },
        { text: 'Description', value: 'benDesc' },
        { text: 'Benefit Limit', value: 'benLim' },
        { text: 'Rate A/E', value: 'premRateAeFormatted' },
        { text: 'Rate %', value: 'premRatePct' },
        { text: 'GAP A/E', value: 'gapAeFormatted' },
        { text: 'GAP', value: 'gap' },
        { text: 'PD A/E/P', value: 'premDueAepFormatted' },
        { text: 'PD', value: 'premDue' }
      ],
      itemList: [],
      loading: false,
      policy: {
        loading: false
      },
      promptRemoveBen: undefined,
      selectedBen: undefined,
      deleteBen: undefined,
      benList: [],
      tarfBenList: [],
      mainForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        itemNo: undefined,
        seqNo: undefined,
        benCode: undefined,
        premRatePct: undefined,
        benDesc: undefined,
        benLim: undefined,
        premRateAe: undefined,
        multiplier: undefined,
        gapAe: undefined,
        gap: undefined,
        premDueAep: undefined,
        premDue: undefined,
        discLdg1: undefined,
        discLdg2: undefined,
        discLdg3: undefined,
        unClmBenLim: undefined,
        unClmOthlim: undefined,
        benSi: undefined,
        napSubLvl: undefined,
        isOwnSi: undefined,
        benLongDesc: undefined
      },
      resetForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        itemNo: undefined,
        seqNo: undefined,
        benCode: undefined,
        premRatePct: undefined,
        benDesc: undefined,
        benLim: undefined,
        premRateAe: undefined,
        multiplier: undefined,
        gapAe: undefined,
        gap: undefined,
        premDueAep: undefined,
        premDue: undefined,
        discLdg1: undefined,
        discLdg2: undefined,
        discLdg3: undefined,
        unClmBenLim: undefined,
        unClmOthlim: undefined,
        benSi: undefined,
        napSubLvl: undefined,
        isOwnSi: undefined,
        benLongDesc: undefined
      },
      cloneMainForm: {},
      showBen: false,
      actionMode: undefined,
      showErrorDialog: false,
      bPackage: undefined,
      cRiskBen: undefined,
      cTarf: undefined,
      dSI: undefined,
      dNAPTotal: 0,
      ddEffDate: undefined,
      iNAPSubLvl: undefined,
      disableBenCode: undefined,
      disableBenLim: undefined,
      siExRate: undefined,
      maxLim: undefined,
      minLim: undefined,
      protPrem: undefined,
      gapLoading: false,
      premDueLoading: false,
      disableBenefit: false,
      benefitCode: undefined
    }
  },
  props: {
    polItem: Object,
    isPkgScheme: Boolean,
    multiRiskBen: String,
    hideCRUD: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    polMaster() {
      return this.$store.state.underwriting.policy.polMaster
    },
    selectedRiskGrpNo() {
      return this.$store.state.underwriting.policy.selectedRiskGrpNo
    },
    selectedRiskNo() {
      return this.$store.state.underwriting.policy.selectedRiskNo
    },
    getNewBen() {
      return this.itemList.filter((item) => {
        return item.newBen === true
      })
    },
    getBen() {
      return this.benList.filter((ben) => {
        return ben.value === this.mainForm.benCode
      })
    },
    displayHeaders() {
      if (this.hideCRUD) {
        return this.headersWOAction
      } else {
        return this.headers
      }
    },
    enableBenBtn() {
      // 2021-04-22 LYT OS-765
      // 01Apr2021
      // Enable only if
      // i. [polMaster.scheme] not blank, bPackage is Yes and [xScheme.multiRiskBen] is “B”, or
      // ii. [polMaster.scheme] is not blank and bPackage is NO
      // iii. [polMaster.scheme] is blank
      return (
        (this.polMaster.scheme !== '' && this.bPackage === true && this.multiRiskBen === 'B') ||
        (this.polMaster.scheme !== '' && (this.bPackage === false || this.bPackage === undefined)) ||
        this.polMaster.scheme === ''
      )
    },
    aeList() {
      return [
        { label: 'A', value: true },
        { label: 'E', value: false }
      ]
    },
    aepList() {
      return [
        { label: 'A', value: 'A' },
        { label: 'E', value: 'E' },
        { label: 'P', value: 'P' }
      ]
    },
    disablePd() {
      return this.mainForm.premDueAep === 'A'
    },
    displayErrorDialog() {
      return this.messages
    },
    valBenLim() {
      if (this.bPackage === true && this.cRiskBen === 'B') {
        var benLim = this.mainForm.benLim
        if (removeFormatNumber(benLim) > this.maxLim) {
          return 'valField:' + 'Must be less than Maximum Benefit Limit on Package Item Benefit.(10474)'
        } else if (removeFormatNumber(benLim) < this.minLim) {
          return 'valField:' + 'Must be more than Minimum Benefit limit on Package Item Benefit.(10475)'
        } else {
          return ''
        }
      }
      return ''
    },
    disableAe() {
      if (this.bPackage === true && this.cRiskBen === 'R') {
        return this.protPrem
      } else {
        return false
      }
    }
  },
  methods: {
    async doInit() {
      this.getPolItemBenParams().then(() => {
        this.getPolItemBenList()
      })
      this.showBen = false
    },
    async getPolItemBenParams() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.polItemBenParams
      if (apiUrl) {
        try {
          this.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.polItem.riskGrpNo,
            riskNo: this.polItem.riskNo
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))

          if (result.data.benList) {
            this.$set(this, 'benList', result.data.benList)
            this.$set(this, 'tarfBenList', result.data.benList)
            this.bPackage = result.data.bPackage // 2021-04-22 LYT OS-765
          }
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async getPolItemBenList() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.polItemBenList
      if (apiUrl) {
        try {
          this.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.polItem.riskGrpNo,
            riskNo: this.polItem.riskNo,
            itemNo: this.polItem.itemNo
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))

          this.displayList(result).then(() => {
            this.itemList.filter((item) => {
              if (item.benCode === this.benefitCode) {
                this.setBenDetails(item)
              }
            })
            this.loading = false
          })
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async displayList(result) {
      const { data } = result
      if (data) {
        this.itemList = []
        data.data.map((item) => {
          const convertItem = {
            polNo: item.polNo,
            renCnt: item.renCnt,
            endtCnt: item.endtCnt,
            riskGrpNo: item.riskGrpNo,
            riskNo: item.riskNo,
            itemNo: item.itemNo,
            seqNo: item.seqNo,
            benCode: item.benCode,
            premRatePct: item.premRatePct.toFixed(5),
            benDesc: item.benDesc,
            benLim: formatPrice(item.benLim),
            premRateAe: item.premRateAe,
            premRateAeFormatted: this.displayAE(item.premRateAe),
            multiplier: item.multiplier.toFixed(2),
            gapAe: item.gapAe,
            gapAeFormatted: this.displayAE(item.gapAe),
            gap: formatPrice(item.gap),
            premDueAep: item.premDueAep,
            premDueAepFormatted: this.displayAEP(item.premDueAep),
            premDue: formatPrice(item.premDue),
            discLdg1: item.discLdg1.toFixed(2),
            discLdg2: item.discLdg2.toFixed(2),
            discLdg3: item.discLdg3.toFixed(2),
            unClmBenLim: item.unClmBenLim.toFixed(2),
            unClmOthlim: item.unClmOthlim.toFixed(2),
            benSi: item.benSi.toFixed(2),
            napSubLvl: item.napSubLvl,
            isOwnSi: item.isOwnSi,
            newBen: item.newBen,
            benLongDesc: item.benLongDesc
          }
          this.itemList.push(convertItem)
        })
      } else {
        this.itemList = []
      }
      return true
    },
    resetItemBenForm() {
      this.benefitCode = undefined
      var thisPage = this
      Object.keys(this.mainForm).forEach(function (key, index) {
        thisPage.mainForm[key] = undefined
      })
      this.disableBenefit = false
    },
    async doCloseItemBen() {
      this.$emit('hidePanel', 'RESET')
      this.showBen = false
      this.resetItemBenForm()
    },
    async showAddBenForm() {
      this.$emit('hidePanel', 'PIB')
      this.benList = this.tarfBenList
      this.showBen = true
      this.actionMode = 'New'
      this.resetItemBenForm()
    },
    // AR: 14-Mar-2020 Added to check if Benefit already exists, if No, create record and calculate
    async calculateItemBen() {
      this.policy.loading = true
      if (this.mainForm.benCode !== '' && this.mainForm.benCode !== undefined && this.actionMode === 'New') {
        const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.calculateItemBen
        if (apiUrl) {
          try {
            this.policy.loading = true
            const requestParams = {
              polNo: this.polMaster.polNo,
              renCnt: this.polMaster.renCnt,
              endtCnt: this.polMaster.endtCnt,
              riskGrpNo: this.polItem.riskGrpNo,
              riskNo: this.polItem.riskNo,
              itemNo: this.polItem.itemNo,
              benCode: this.mainForm.benCode,
              bPackage: this.bPackage
            }
            const response = await this.$http.post(apiUrl, requestParams)
            this.displayList(response).then(() => {
              this.policy.loading = false
              this.doSelectBen(this.getNewBen[0])
              this.$emit('itemList')
            })
          } catch (err) {
            const { data } = err.response
            if (data) {
              this.message(data)
              this.fieldMessage(data)
              this.policy.loading = false
              this.mainForm.benCode = ''
            }
          }
        } else {
          this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        }
      } else if (this.mainForm.benCode !== '' && this.mainForm.benCode !== undefined && this.actionMode === 'Review') {
        if (this.mainForm.benCode !== this.mainForm.benCodeOrig) {
          let isExists = false
          this.itemList.filter((item) => {
            if (item.benCode === this.mainForm.benCode) {
              this.$toast.error('The risk item benefit you selected already exists.')
              isExists = true
            }
          })
          if (!isExists) {
            this.doSave()
          } else {
            this.mainForm.benCode = ''
          }
        }
      }
      this.policy.loading = false
    },
    async doAddBen() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.addBen
      if (apiUrl) {
        try {
          this.policy.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.polItem.riskGrpNo,
            riskNo: this.polItem.riskNo,
            itemNo: this.polItem.itemNo,
            bPackage: this.bPackage
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))

          this.displayList(result).then(() => {
            this.policy.loading = false
            this.doSelectBen(this.getNewBen[0])
          })
          this.$toast.success('Item Benefit successfully added.')
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
            this.policy.loading = false
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async doPremRateAe() {
      if (this.mainForm.premRateAe) {
        if (this.mainForm.premDueAep === 'A') {
          this.premDueLoading = true
        }
        this.mainForm.premRatePct = this.defaultAmount()
        await this.getGap()
        this.doPremDueAep()
      } else {
        this.calcGap()
      }
    },
    calcGap() {
      if (this.mainForm.gapAe) {
        // 2021-04-08 LYT OS-734
        this.mainForm.gap = (this.dSI * (this.mainForm.premRatePct / 100)) / this.polMaster.billCurrRate
        if (this.polMaster.billCoIns === 'N') {
          this.mainForm.gap = this.mainForm.gap * ((100 - this.polMaster.coInsPct)) / 100
        }
        this.mainForm.gap = formatPrice(this.mainForm.gap)
      }
      this.doPremDueAep()
    },
    async getGap() {
      if (this.mainForm.gapAe) {
        const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.getGap
        if (apiUrl) {
          try {
            this.gapLoading = true
            const expand = {
              polNo: this.mainForm.polNo,
              renCnt: this.mainForm.renCnt,
              endtCnt: this.mainForm.endtCnt,
              riskGrpNo: this.mainForm.riskGrpNo,
              riskNo: this.mainForm.riskNo,
              itemNo: this.mainForm.itemNo,
              benCode: this.mainForm.benCode,
              bPackage: this.bPackage,
              cTarf: this.cTarf,
              ddEffDate: new Date(this.ddEffDate)
            }
            const result = await this.$http.get(expandURI(apiUrl, expand))

            if (result.data) {
              this.dNAPTotal = result.data.dNAPTotal
              this.mainForm.gap = formatPrice(result.data.gap)
            }
            this.gapLoading = false
          } catch (err) {
            this.gapLoading = false
            const { data } = err.response
            if (data) {
              this.message(data)
            }
          }
        } else {
          this.$toast.error('The request failed because it is trying to execute an invalid API url.')
        }
      }
    },
    doPremDueAep() {
      if (this.mainForm.premDueAep === 'A') {
        this.getPremDue()
      }
    },
    async getPremDue() {
      this.policy.loading = true
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.getPremDue
      if (apiUrl) {
        try {
          this.premDueLoading = true

          if (this.mainForm.gap === '') {
            this.mainForm.gap = 0
          }
          const expand = {
            polNo: this.mainForm.polNo,
            renCnt: this.mainForm.renCnt,
            endtCnt: this.mainForm.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo,
            itemNo: this.mainForm.itemNo,
            benCode: this.mainForm.benCode,
            bPackage: this.bPackage,
            gap: removeFormatNumber(this.mainForm.gap),
            dNAPTotal: this.dNAPTotal
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))

          if (result.data) {
            this.dNAPTotal = result.data.dNAPTotal
            this.mainForm.premDue = formatPrice(result.data.premDue)
          }
          this.premDueLoading = false
          this.policy.loading = false
        } catch (err) {
          this.premDueLoading = false
          this.policy.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doSelectBen(data) {
      this.setBenDetails(data)
      this.showBen = true
      this.actionMode = 'Review'
      this.editPolItemBen()
      this.disableBenefit = true
      this.$emit('hidePanel', 'PIB')
    },
    defaultAmount() {
      return (0.0).toFixed(5)
    },
    setBenDetails(data) {
      this.mainForm.polNo = data.polNo
      this.mainForm.renCnt = data.renCnt
      this.mainForm.endtCnt = data.endtCnt
      this.mainForm.riskGrpNo = data.riskGrpNo
      this.mainForm.riskNo = data.riskNo
      this.mainForm.itemNo = data.itemNo
      this.mainForm.seqNo = data.seqNo
      this.mainForm.benCode = data.benCode
      this.mainForm.benCodeOrig = data.benCode
      this.mainForm.premRatePct = data.premRatePct
      this.mainForm.benDesc = data.benDesc
      this.mainForm.benLim = data.benLim
      this.mainForm.premRateAe = data.premRateAe
      this.mainForm.multiplier = data.multiplier
      this.mainForm.gapAe = data.gapAe
      this.mainForm.gap = data.gap
      this.mainForm.premDueAep = data.premDueAep
      this.mainForm.premDue = data.premDue
      this.mainForm.discLdg1 = data.discLdg1
      this.mainForm.discLdg2 = data.discLdg2
      this.mainForm.discLdg3 = data.discLdg3
      this.mainForm.unClmBenLim = data.unClmBenLim
      this.mainForm.unClmOthlim = data.unClmOthlim
      this.mainForm.benSi = data.benSi
      this.mainForm.napSubLvl = data.napSubLvl
      this.mainForm.isOwnSi = data.isOwnSi
      this.mainForm.benLongDesc = data.benLongDesc

      this.resetForm = clone(this.mainForm)
      this.cloneMainForm = clone(this.mainForm)
    },
    doPromptRemoveBen(data) {
      this.selectedBen = data
      this.deleteBen = 'Are you sure you want to delete benefit ' + data.benCode + '?'
      this.promptRemoveBen = true
    },
    async doRemoveBen() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.delBen
      if (apiUrl) {
        try {
          this.policy.loading = true
          const expand = {
            polNo: this.selectedBen.polNo,
            renCnt: this.selectedBen.renCnt,
            endtCnt: this.selectedBen.endtCnt,
            riskGrpNo: this.selectedBen.riskGrpNo,
            riskNo: this.selectedBen.riskNo,
            itemNo: this.selectedBen.itemNo,
            seqNo: this.selectedBen.seqNo,
            benCode: this.selectedBen.benCode
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))

          this.displayList(result).then(() => {
            this.policy.loading = false
            this.$emit('itemList')
          })
          this.$toast.success('Benefit record successfully deleted.')
          // lyt
          this.mainForm.seqNo = undefined
          this.showBen = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    async doSave() {
      this.resetMessages()
      let output = 'SUCCESS'

      if (isEqual(this.cloneMainForm, this.mainForm)) {
        this.doCloseItemBen()
        return 'NOCHANGE'
      }

      /** this.$validator.validateAll().then((result) => {
        if (result) {
          this.policy.loading = true
          this.$parent.$parent.$parent.$parent.$parent.$parent.$parent.policy.saving = true
          this.doPremRateAe()
          this.doPolItemBenSave().then((success) => {
            if (success) {
              this.getPolItemBenList()
              this.$emit('itemList')
              this.$toast.success('Benefit record successfully saved.')
              this.doCloseItemBen()
              this.$emit('hidePanel', 'RESET')
            } else {
              output = 'ERROR'
              this.$toast.error('An unexpected error occured benefit record saving failed.')
            }
            this.policy.loading = false
            this.$parent.$parent.$parent.$parent.$parent.$parent.$parent.policy.saving = false
          })
        } else {
          this.promptFieldCheck()
        }
      }) **/

      let result = await this.$validator.validateAll()
      if (result) {
        this.policy.loading = true
        this.$parent.$parent.$parent.$parent.$parent.$parent.$parent.policy.saving = true
        await this.doPremRateAe()
        let success = await this.doPolItemBenSave()
        if (success) {
          this.getPolItemBenList()
          this.$emit('itemList')
          this.$toast.success('Benefit record successfully saved.')
          this.doCloseItemBen()
          this.$emit('hidePanel', 'RESET')
        } else {
          output = 'ERROR'
          this.$toast.error('An unexpected error occured benefit record saving failed.')
        }
        this.policy.loading = false
        this.$parent.$parent.$parent.$parent.$parent.$parent.$parent.policy.saving = false
      } else {
        this.promptFieldCheck()
      }

      return output
    },
    doResetBen() {
      this.mainForm = clone(this.resetForm)
      this.cloneMainForm = clone(this.resetForm)
    },
    async doPolItemBenSave() {
      try {
        if (this.mainForm.polNo == null) {
          return true
        }
        this.benefitCode = this.mainForm.benCode
        this.mainForm.benLim = removeFormatNumber(this.mainForm.benLim)
        this.mainForm.premRatePct = removeFormatNumber(this.mainForm.premRatePct)
        this.mainForm.gap = removeFormatNumber(this.mainForm.gap)
        this.mainForm.premDue = removeFormatNumber(this.mainForm.premDue)
        if (this.getBen.length > 0) {
          // this.mainForm.benDesc = this.getBen[0].secondaryValue
          this.mainForm.benLongDesc = this.getBen[0].secondaryValue
        } else {
          // this.mainForm.benDesc = ''
          this.mainForm.benLongDesc = ''
        }
        return await this.$store.dispatch('savePolItemBen', this.mainForm)
      } catch (err) {
        const { data } = err.response
        if (data) {
          this.message(data)
          this.fieldMessage(data)
        }
      }
    },
    doInitForm() {
      this.mainForm.polNo = undefined
      this.mainForm.renCnt = undefined
      this.mainForm.endtCnt = undefined
      this.mainForm.riskGrpNo = undefined
      this.mainForm.riskNo = undefined
      this.mainForm.itemNo = undefined
      this.mainForm.seqNo = undefined
      this.mainForm.benCode = undefined
      this.mainForm.premRatePct = undefined
      this.mainForm.benDesc = undefined
      this.mainForm.benLim = undefined
      this.mainForm.premRateAe = undefined
      this.mainForm.multiplier = undefined
      this.mainForm.gapAe = undefined
      this.mainForm.gap = undefined
      this.mainForm.premDueAep = undefined
      this.mainForm.premDue = undefined
      this.mainForm.discLdg1 = undefined
      this.mainForm.discLdg2 = undefined
      this.mainForm.discLdg3 = undefined
      this.mainForm.unClmBenLim = undefined
      this.mainForm.unClmOthlim = undefined
      this.mainForm.benSi = undefined
      this.mainForm.napSubLvl = undefined
      this.mainForm.isOwnSi = undefined
      this.mainForm.benLongDesc = undefined

      this.resetForm = clone(this.mainForm)
      this.cloneMainForm = clone(this.mainForm)
    },
    displayAE(data) {
      if (data === true) {
        return 'A'
      } else {
        return 'E'
      }
    },
    displayAEP(data) {
      if (data === 'A') {
        return 'A'
      } else if (data === 'E') {
        return 'E'
      } else if (data === 'P') {
        return 'P'
      }
    },
    async editPolItemBen() {
      const apiUrl = this.$store.state.underwriting.policyRiskItemSettings.menu.editPolItemBen
      if (apiUrl) {
        try {
          this.loading = true
          const expand = {
            polNo: this.mainForm.polNo,
            renCnt: this.mainForm.renCnt,
            endtCnt: this.mainForm.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo,
            itemNo: this.mainForm.itemNo,
            seqNo: this.mainForm.seqNo,
            benCode: this.mainForm.benCode
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result.data) {
            // 2020-09-21 LYT OS-450
            if (result.data.benList !== null) {
              this.$set(this, 'benList', result.data.benList)
            }
            this.bPackage = result.data.bPackage
            this.cRiskBen = result.data.cRiskBen
            this.cTarf = result.data.cTarf
            this.dSI = result.data.dSI
            this.dNAPTotal = result.data.dNAPTotal
            this.ddEffDate = result.data.ddEffDate
            this.iNAPSubLvl = result.data.iNAPSubLvl
            this.disableBenLim = result.data.disableBenLim
            this.siExRate = result.data.siExRate
            this.maxLim = result.data.maxLim
            this.minLim = result.data.minLim
            this.protPrem = result.data.protPrem
            if (this.bPackage === true && this.cRiskBen === 'R') {
              this.disableBenCode = true
              // 2020-09-21 LYT OS-450
              this.benList = []
              const benItem = {
                value: this.mainForm.benCode,
                // label: this.mainForm.benCode + ' - ' + this.mainForm.benDesc,
                // secondaryValue: this.mainForm.benDesc // 2020-11-12 LYT OS-568
                label: this.mainForm.benCode + ' - ' + this.mainForm.benLongDesc,
                secondaryValue: this.mainForm.benLongDesc
              }
              this.benList.push(benItem)
            } else {
              this.disableBenCode = false
            }

            if (this.protPrem === true) {
              this.mainForm.premRateAe = true
            }
          }
          this.doPremRateAe()
          this.doPremRatePct()
          this.loading = false
        } catch (err) {
          this.loading = false
          const { data } = err.response
          if (data) {
            this.notify(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    },
    doBenLim() {
      this.dSI = removeFormatNumber(this.mainForm.benLim) * this.siExRate
    },
    doPremRatePct() {
      if (!this.mainForm.premRateAe) {
        this.calcGap()
      }
    },
    async doGapAe() {
      if (this.mainForm.gapAe) {
        if (this.mainForm.premDueAep === 'A') {
          this.premDueLoading = true
        }
        if (!this.mainForm.premRateAe) {
          this.calcGap()
        } else {
          await this.getGap()
          this.doPremDueAep()
        }
      }
    }
  },
  watch: {
    displayErrorDialog: {
      handler: function (messages) {
        if (messages && messages.length > 0) {
          this.showErrorDialog = true
        }
      },
      deep: true
    }
  },
  beforeMount() {
    if (this.$store.state.underwriting.policyRiskItemSettings.init === 1) {
      // this.doInit()
    }
  },
  created() {
    // Local validator
    const valField = {
      getMessage: (field, args) => {
        if (args === void 0) args = []
        var errMsg = args[0]
        return errMsg
      },
      validate: (value, args) => {
        if (value) {
          if (args === void 0) args = []
          var errMsg = args[0]
          return errMsg
        } else return true
      }
    }
    this.$validator.extend('valField', valField)
  },
  mixins: [TableConst.default, MessageMixin]
}
</script>
